// src/components/membership/MembershipPlans-HomeUpgradePrompt.js
// =============================================================================
// MembershipPlansHomeUpgradePrompt (strict, hooks-safe)
// - Requires authoritative `subscriptionStatus` from MembershipPlans-SubscriptionStatus
// - Renders as a modal dialog overlay
// - "Maybe Later" dismisses for ~3 months via localStorage
// - CTA buttons close the dialog immediately before navigating
// =============================================================================

import React, { useLayoutEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faXmark } from "@fortawesome/free-solid-svg-icons";

import "./MembershipPlans-ActiveSubHeader.css";
import "./MembershipPlans-HomeUpgradePrompt.css";
import { MembershipPlansEmojiPicker } from "./MembershipPlans-EmojiPicker.js";

import {
    PLAN_ID,
    getPlanTitle,
    getFeaturesToUnlock,
    getOilBaronUpsellCheckoutItems,
} from "./MembershipPlans-SubscriptionStatus.js";

import TOTLogo from "../../assets/brand/ColorLogo_ImageOnly.png";

const HIDE_UNTIL_KEY = "totdata_home_upgrade_prompt_hide_until_ms";
const THREE_MONTHS_MS = 1000 * 60 * 60 * 24 * 90;

const CHECKOUT_FLOW_STORAGE_KEY = "totdata_checkout_flow";
const POST_CHECKOUT_RETURN_STORAGE_KEY = "totdata_post_checkout_return";

function safeParseInt(value) {
    const n = Number.parseInt(String(value || ""), 10);
    return Number.isFinite(n) ? n : null;
}

function getHideUntilMs() {
    try {
        return safeParseInt(window.localStorage?.getItem?.(HIDE_UNTIL_KEY));
    } catch {
        return null;
    }
}

function setHideUntilMs(ms) {
    try {
        window.localStorage?.setItem?.(HIDE_UNTIL_KEY, String(ms));
    } catch {
        // ignore
    }
}

function getTrialDaysText(trialEndMs) {
    if (!Number.isFinite(trialEndMs)) return "Trial";
    const remainingMs = trialEndMs - Date.now();
    const remainingDays = Math.max(0, Math.ceil(remainingMs / (1000 * 60 * 60 * 24)));
    return `${remainingDays} day${remainingDays === 1 ? "" : "s"} left`;
}

export function MembershipPlansHomeUpgradePrompt({
    subscriptionStatus,
    accountTypeId = 0,
    targetPlanId = PLAN_ID.oilBaron,
    plansPath = "/membership-plans",
    paymentPath = "/payment",
    billingPeriod = 1,
    postCheckoutReturnPath = "/membership-plans",
    baseUrl = "",
}) {
    const navigate = useNavigate();
    const badgeRef = useRef(null);
    const [badgeWidthPx, setBadgeWidthPx] = useState(null);
    const [dismissed, setDismissed] = useState(false);

    const hasStatus = Boolean(subscriptionStatus);

    const currentPlanId = useMemo(() => {
        return (subscriptionStatus?.effectivePlanId || PLAN_ID.wildcatter).toString();
    }, [subscriptionStatus?.effectivePlanId]);

    const isOilBaronTrial = useMemo(() => {
        return Boolean(subscriptionStatus?.isTrialActive) && currentPlanId === PLAN_ID.oilBaron;
    }, [subscriptionStatus?.isTrialActive, currentPlanId]);

    const promptSourcePlanId = useMemo(() => {
        return isOilBaronTrial ? PLAN_ID.wildcatter : currentPlanId;
    }, [isOilBaronTrial, currentPlanId]);

    const memberTypeLabel = useMemo(() => {
        if (isOilBaronTrial) return `Trial | ${getTrialDaysText(subscriptionStatus?.trialEndMs)}`;
        const hidePaidLabel = currentPlanId === PLAN_ID.blackgoldRevenue || currentPlanId === PLAN_ID.blackgoldJib || currentPlanId === PLAN_ID.oilBaron || currentPlanId === PLAN_ID.bigRig;
        if (hidePaidLabel) return "";
        return (subscriptionStatus?.memberTypeLabel || (currentPlanId !== PLAN_ID.wildcatter ? "Paid Member" : "Free Member")).toString();
    }, [subscriptionStatus?.memberTypeLabel, subscriptionStatus?.trialEndMs, currentPlanId, isOilBaronTrial]);

    const targetPlanLabel = useMemo(() => getPlanTitle(targetPlanId), [targetPlanId]);
    const featuresToUnlock = useMemo(() => getFeaturesToUnlock(promptSourcePlanId, targetPlanId), [promptSourcePlanId, targetPlanId]);

    const shouldHide = useMemo(() => {
        const hideUntil = getHideUntilMs();
        if (hideUntil && Date.now() < hideUntil) return true;
        return dismissed;
    }, [dismissed]);

    useLayoutEffect(() => {
        if (!badgeRef.current || typeof ResizeObserver === "undefined") return;
        const el = badgeRef.current;

        const update = () => {
            const w = el?.offsetWidth || null;
            if (w && w !== badgeWidthPx) setBadgeWidthPx(w);
        };

        update();
        const ro = new ResizeObserver(update);
        ro.observe(el);
        return () => ro.disconnect();
    }, [badgeWidthPx]);

    if (!hasStatus && typeof process !== "undefined" && process.env?.NODE_ENV !== "production") {
        throw new Error("MembershipPlansHomeUpgradePrompt requires subscriptionStatus");
    }

    if (!hasStatus) return null;

    const hideForPlan = promptSourcePlanId === PLAN_ID.oilBaron || promptSourcePlanId === PLAN_ID.bigRig;
    const hideForAccountType = Number(accountTypeId) === 2;
    if (shouldHide) return null;
    if (hideForPlan) return null;
    if (hideForAccountType) return null;
    if (!featuresToUnlock || featuresToUnlock.length === 0) return null;

    const maybeLaterRoomPx = 150;
    const desiredCardWidthPx = badgeWidthPx ? badgeWidthPx + 60 + maybeLaterRoomPx : null;

    const currentPlanText = memberTypeLabel ? `${getPlanTitle(currentPlanId)} | ${memberTypeLabel}` : getPlanTitle(currentPlanId);

    const closeDialog = () => {
        setDismissed(true);
    };

    const onMaybeLater = () => {
        const hideUntil = Date.now() + THREE_MONTHS_MS;
        setHideUntilMs(hideUntil);
        closeDialog();
    };

    const onUpgradeNow = () => {
        const items = getOilBaronUpsellCheckoutItems(promptSourcePlanId);

        try {
            window.sessionStorage?.setItem?.(POST_CHECKOUT_RETURN_STORAGE_KEY, String(postCheckoutReturnPath));
            window.sessionStorage?.setItem?.(CHECKOUT_FLOW_STORAGE_KEY, "new");
        } catch {
            // ignore
        }

        closeDialog();
        navigate(paymentPath, {
            state: {
                billingPeriod,
                items,
                useNewCheckout: true,
            },
        });
    };

    const onSeeAllPlans = () => {
        closeDialog();
        navigate(plansPath);
    };

    return (
        <div className="mc-home-upgrade-modal" aria-hidden={false}>
            <div className="mc-home-upgrade-backdrop" />

            <div className="mc-home-upgrade-dialog" role="dialog" aria-modal="true" aria-labelledby="mc-home-upgrade-title">
                <div className="mc-home-upgrade-card" style={{ width: desiredCardWidthPx ? `${desiredCardWidthPx}px` : undefined }}>
                    <div className="mc-home-upgrade-dismiss">
                        <button
                            type="button"
                            className="mc-home-upgrade-dismiss-btn"
                            onClick={onMaybeLater}
                            aria-label="Maybe Later"
                            title="Maybe Later"
                        >
                            <FontAwesomeIcon icon={faXmark} />
                            Maybe Later
                        </button>
                    </div>

                    <div className="mc-home-upgrade-top">
                        <div className="mc-home-upgrade-pill-wrap" ref={badgeRef}>
                            <div className="mc-current-sub" aria-label="Current subscription">
                                <div className="mc-current-sub-inner">
                                    <MembershipPlansEmojiPicker
                                        planId={currentPlanId}
                                        isWildcatterCustom={Boolean(currentPlanId === PLAN_ID.wildcatter)}
                                        as="div"
                                        className="mc-current-sub-icon mc-current-sub-icon--lg"
                                        emojiClassName="mc-current-sub-emoji"
                                    />

                                    <div className="mc-current-sub-text">
                                        <div className="mc-current-sub-title">Current subscription</div>
                                        <div className="mc-current-sub-name">{currentPlanText}</div>
                                    </div>

                                    <img className="mc-current-sub-logo" src={TOTLogo} alt="totdata.com" />
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="mc-home-upgrade-body">
                        <div className="mc-home-upgrade-copy">
                            <h2 id="mc-home-upgrade-title" className="mc-home-upgrade-title">Upgrade to {targetPlanLabel} to unlock these features:</h2>

                            <ul className="mc-home-upgrade-list">
                                {featuresToUnlock.map((feature) => (
                                    <li key={feature}>{feature}</li>
                                ))}
                            </ul>
                        </div>
                    </div>

                    <div className="mc-home-upgrade-actions">
                        <button type="button" className="mc-home-upgrade-btn is-upgrade" onClick={onUpgradeNow}>
                            Upgrade Now
                        </button>

                        <button type="button" className="mc-home-upgrade-btn is-plans" onClick={onSeeAllPlans}>
                            See all plans
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default MembershipPlansHomeUpgradePrompt;